import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  ArrowRight, 
  Heart, 
  Shield, 
  Clock, 
  Phone,
  CheckCircle2,
  Stethoscope,
  ShoppingBag,
  FileText,
  Users
} from 'lucide-react';
import { services, testimonials } from '../data/mock';

const iconMap = {
  Stethoscope: Stethoscope,
  Heart: Heart,
  ShoppingBag: ShoppingBag,
  FileText: FileText,
  Package: ShoppingBag,
  Users: Users
};

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="min-h-screen flex items-center pt-20"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-white/40 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-mono uppercase tracking-wide text-[#232323] mb-6">
                <Heart className="w-4 h-4 text-[#e57373]" />
                Caring Support for Seniors
              </span>
            </motion.div>

            <motion.h1
              className="heading-hero mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Personal Assistance for
              <br />
              <span className="text-[#5a5a5a]">Your Loved Ones</span>
            </motion.h1>

            <motion.p
              className="body-large mb-8 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Everyday help for senior citizens — from doctor visits to daily errands. 
              Human, reliable support so your family can feel at ease.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/request-help" className="btn-primary gap-2">
                <Phone className="w-4 h-4" />
                Request Concierge Help
              </Link>
              <a 
                href={`https://web.whatsapp.com/send?phone=919619998944&text=${encodeURIComponent('Hello, I would like to request concierge support for a senior.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary gap-2"
              >
                Chat on WhatsApp
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              className="flex flex-wrap gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center gap-2 text-sm text-[#353535]">
                <CheckCircle2 className="w-5 h-5 text-[#4caf50]" />
                <span>Pay only for what you use</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#353535]">
                <CheckCircle2 className="w-5 h-5 text-[#4caf50]" />
                <span>No app or login needed</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#353535]">
                <CheckCircle2 className="w-5 h-5 text-[#4caf50]" />
                <span>Verified, caring team</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <motion.h2
              className="heading-1 mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Why Families Trust Us
            </motion.h2>
            <motion.p
              className="body-large max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Simple, transparent, and human-centered support designed for seniors and their families.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Human Touch',
                description: 'Real people who care. Our team treats every senior with respect, patience, and kindness.',
                accent: 'accent-pink'
              },
              {
                icon: Shield,
                title: 'Trust & Safety',
                description: 'Background-verified team members. Families stay informed with regular updates.',
                accent: 'accent-green'
              },
              {
                icon: Clock,
                title: 'Flexible & Simple',
                description: 'No subscriptions or commitments. Pay only when you need help. No apps required.',
                accent: 'accent-orange'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                className={`service-card ${item.accent} p-8`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-[#232323]" />
                </div>
                <h3 className="heading-3 mb-3">{item.title}</h3>
                <p className="body-small">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <motion.h2
                className="heading-1 mb-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                How We Can Help
              </motion.h2>
              <motion.p
                className="body-large"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                Everyday assistance tailored to senior needs
              </motion.p>
            </div>
            <Link 
              to="/services" 
              className="btn-secondary gap-2"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <motion.div
                  key={service.id}
                  className={`service-card ${service.accent} p-6`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center mb-4">
                    <IconComponent className="w-5 h-5 text-[#232323]" />
                  </div>
                  <h3 className="heading-3 mb-2">{service.title}</h3>
                  <p className="body-small">{service.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <motion.h2
              className="heading-1 mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              What Families Say
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="text-[#353535] mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-medium text-[#232323]">{testimonial.name}</p>
                  <p className="text-sm text-[#666]">
                    {testimonial.relation} • {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20"
        style={{ background: 'var(--gradient-hero-subtle)' }}
      >
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <motion.h2
            className="heading-1 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p
            className="body-large mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Reach out today. We'll discuss your needs and share the pricing — 
            no commitment required.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/request-help" className="btn-primary gap-2">
              <Phone className="w-4 h-4" />
              Request Help Now
            </Link>
            <Link to="/how-it-works" className="btn-secondary gap-2">
              Learn How It Works
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;